// Wave W-AP-1 — per-category visual identity for the template gallery.
//
// The previous gallery mockups were theme-only (light / midnight / forest /
// coral / mint / magenta) so two cards in the same theme looked identical
// regardless of trade. PR #411 added a thin category-accent stripe but it
// wasn't enough — Alex still read the gallery as "all white cards with blue
// buttons". This module gives every TemplateConfig.category a distinct
// palette + hero treatment + CTA shape, used by `TemplateCardMockup` to
// paint a card that looks like a poster for its trade, not a wireframe.
//
// IMPORTANT: these palettes are for the GALLERY mockup ONLY. The live
// rendered widget still uses the resolved `WidgetTheme` from the
// template's own `theme` field. The mockup palette is a marketing
// composition; the widget theme is the runtime look-and-feel.

export type CategoryStyleId =
  | 'automotive'
  | 'construction'
  | 'cleaning'
  | 'home-improvement'
  | 'emergency'
  | 'outdoor'
  | 'professional'
  | 'default';

export type HeroTreatment =
  | 'dark-mode'         // solid dark hero, light icon on top — Automotive
  | 'diagonal-stripe'   // bold diagonal accent across hero — Construction
  | 'sparkle'           // soft light hero with sparkle dots — Cleaning
  | 'grid-pattern'      // subtle grid overlay — Home Improvement
  | 'chevrons'          // warning chevrons — Emergency
  | 'leaf'              // leaf accent — Outdoor
  | 'geometric';        // minimal squares — Professional

export type CtaShape =
  | 'pill'         // fully rounded — default, friendly
  | 'rounded-sq'   // 6px radius — industrial / construction
  | 'squared';     // 2px radius — minimal professional

/**
 * W-BB-2 — urgency, animation, weight + font tokens used by
 * `deriveStyleFromCategory` to produce a full `AdvStyle` for templates that
 * don't ship with an explicit per-template style. Optional on the type so the
 * existing `default` family doesn't have to set them (it falls back to its
 * own subtle baseline inside `deriveStyleFromCategory`).
 */
export type CategoryUrgency = 'low' | 'medium' | 'high';
export type CategoryAnimation = 'fade' | 'slide' | 'slide-fade';
export type CategoryHeadingWeight = 600 | 700 | 800;
export type CategoryFontFamily =
  | 'system' | 'jakarta' | 'satoshi' | 'geist' | 'inter' | 'manrope';

export interface CategoryStyle {
  id: CategoryStyleId;
  /** Hero background colour — fills the top ~55% of the mockup. */
  heroBg: string;
  /** Foreground / accent colour painted over the hero. */
  heroAccent: string;
  /** Card body background (below the hero). */
  bodyBg: string;
  /** Body text / row colour. */
  bodyRow: string;
  /** CTA fill — usually heroAccent with a gradient companion. */
  ctaFrom: string;
  ctaTo: string;
  /** CTA text colour. */
  ctaText: string;
  /** CTA accent guaranteed ≥4.5:1 on white — used for small-text buttons. */
  ctaOnWhite: string;
  /** Hero composition style. */
  hero: HeroTreatment;
  /** CTA button shape. */
  ctaShape: CtaShape;
  /** Whether the hero is on a dark base (icon needs light tinting). */
  isDark: boolean;
  /* ─── W-BB-2 — derived-style tokens (live widget identity) ─── */
  /** Gradient start colour for the widget body in the live renderer. */
  bgFromHex?: string;
  /** Gradient end colour for the widget body in the live renderer. */
  bgToHex?: string;
  /** Accent colour applied to the result panel. */
  accent?: string;
  /** Urgency tier — drives result-panel emphasis + border choice. */
  urgency?: CategoryUrgency;
  /** Step-transition animation style. */
  animationStyle?: CategoryAnimation;
  /** Heading font-weight (600 / 700 / 800). */
  headingWeight?: CategoryHeadingWeight;
  /** Font-family token consumed by the widget renderer. */
  fontFamily?: CategoryFontFamily;
}

const STYLES: Record<CategoryStyleId, CategoryStyle> = {
  automotive: {
    id: 'automotive',
    heroBg: '#0f172a',          // slate-900
    heroAccent: '#fb923c',       // orange-400
    bodyBg: '#ffffff',
    bodyRow: '#e2e8f0',
    ctaFrom: '#f97316',          // orange-500
    ctaTo: '#ea580c',            // orange-600
    ctaText: '#ffffff',
    ctaOnWhite: '#c2410c',
    hero: 'dark-mode',
    ctaShape: 'pill',
    isDark: true,
    bgFromHex: '#0f172a',
    bgToHex: '#1e293b',
    accent: '#fb923c',
    urgency: 'high',
    animationStyle: 'slide-fade',
    headingWeight: 800,
    fontFamily: 'geist',
  },
  construction: {
    id: 'construction',
    heroBg: '#1e293b',           // slate-800
    heroAccent: '#fbbf24',       // amber-400
    bodyBg: '#fffbeb',           // amber-50
    bodyRow: '#fde68a',          // amber-200
    ctaFrom: '#f59e0b',          // amber-500
    ctaTo: '#d97706',            // amber-600
    ctaText: '#1e293b',
    ctaOnWhite: '#92400e',
    hero: 'diagonal-stripe',
    ctaShape: 'rounded-sq',
    isDark: true,
    bgFromHex: '#1c1917',
    bgToHex: '#292524',
    accent: '#f59e0b',
    urgency: 'medium',
    animationStyle: 'slide',
    headingWeight: 700,
    fontFamily: 'satoshi',
  },
  cleaning: {
    id: 'cleaning',
    heroBg: '#ecfdf5',           // emerald-50
    heroAccent: '#10b981',       // emerald-500
    bodyBg: '#ffffff',
    bodyRow: '#d1fae5',          // emerald-100
    ctaFrom: '#10b981',          // emerald-500
    ctaTo: '#059669',            // emerald-600
    ctaText: '#ffffff',
    ctaOnWhite: '#047857',
    hero: 'sparkle',
    ctaShape: 'pill',
    isDark: false,
    bgFromHex: '#ecfdf5',
    bgToHex: '#d1fae5',
    accent: '#10b981',
    urgency: 'low',
    animationStyle: 'fade',
    headingWeight: 600,
    fontFamily: 'jakarta',
  },
  'home-improvement': {
    id: 'home-improvement',
    heroBg: '#eff6ff',           // blue-50
    heroAccent: '#2563eb',       // blue-600
    bodyBg: '#ffffff',
    bodyRow: '#dbeafe',          // blue-100
    ctaFrom: '#2563eb',          // blue-600
    ctaTo: '#1d4ed8',            // blue-700
    ctaText: '#ffffff',
    ctaOnWhite: '#1d4ed8',
    hero: 'grid-pattern',
    ctaShape: 'pill',
    isDark: false,
    bgFromHex: '#f0f9ff',
    bgToHex: '#dbeafe',
    accent: '#2563eb',
    urgency: 'medium',
    animationStyle: 'fade',
    headingWeight: 700,
    fontFamily: 'inter',
  },
  emergency: {
    id: 'emergency',
    heroBg: '#7f1d1d',           // red-900
    heroAccent: '#fbbf24',       // amber-400
    bodyBg: '#fef2f2',           // red-50
    bodyRow: '#fecaca',          // red-200
    ctaFrom: '#dc2626',          // red-600
    ctaTo: '#991b1b',            // red-800
    ctaText: '#ffffff',
    ctaOnWhite: '#b91c1c',
    hero: 'chevrons',
    ctaShape: 'pill',
    isDark: true,
    bgFromHex: '#fef3c7',
    bgToHex: '#fed7aa',
    accent: '#dc2626',
    urgency: 'high',
    animationStyle: 'slide-fade',
    headingWeight: 800,
    fontFamily: 'manrope',
  },
  outdoor: {
    id: 'outdoor',
    heroBg: '#14532d',           // green-900
    heroAccent: '#bef264',       // lime-300
    bodyBg: '#f7fee7',           // lime-50
    bodyRow: '#d9f99d',          // lime-200
    ctaFrom: '#16a34a',          // green-600
    ctaTo: '#15803d',            // green-700
    ctaText: '#ffffff',
    ctaOnWhite: '#166534',
    hero: 'leaf',
    ctaShape: 'pill',
    isDark: true,
    bgFromHex: '#f0fdf4',
    bgToHex: '#d1fae5',
    accent: '#16a34a',
    urgency: 'low',
    animationStyle: 'slide',
    headingWeight: 700,
    fontFamily: 'jakarta',
  },
  professional: {
    id: 'professional',
    heroBg: '#faf5ff',           // purple-50
    heroAccent: '#7c3aed',       // violet-600
    bodyBg: '#fffbeb',           // cream
    bodyRow: '#ede9fe',          // violet-100
    ctaFrom: '#7c3aed',          // violet-600
    ctaTo: '#6d28d9',            // violet-700
    ctaText: '#ffffff',
    ctaOnWhite: '#6d28d9',
    hero: 'geometric',
    ctaShape: 'squared',
    isDark: false,
    bgFromHex: '#faf5ff',
    bgToHex: '#ede9fe',
    accent: '#7c3aed',
    urgency: 'medium',
    animationStyle: 'fade',
    headingWeight: 600,
    fontFamily: 'satoshi',
  },
  default: {
    id: 'default',
    heroBg: '#f1f5f9',           // slate-100
    heroAccent: '#475569',       // slate-600
    bodyBg: '#ffffff',
    bodyRow: '#e2e8f0',
    ctaFrom: '#475569',
    ctaTo: '#334155',
    ctaText: '#ffffff',
    ctaOnWhite: '#334155',
    hero: 'geometric',
    ctaShape: 'pill',
    isDark: false,
  },
};

/**
 * Map a `TemplateConfig.category` string to one of the curated style
 * families. The category set in templatePresets has expanded over time
 * (Driveway, HVAC, Restoration, Renewable Energy, etc.); we collapse
 * them into the 7 visual families so the gallery stays coherent.
 */
export function getCategoryStyle(category: string | undefined): CategoryStyle {
  if (!category) return STYLES.default;
  const c = category.toLowerCase();
  // Building-systems trades (HVAC / plumbing / electrical / 'Mechanical',
  // incl. 'HVAC & Mechanical') belong to the home-improvement family. This
  // MUST be tested before the automotive branch: 'HVAC & Mechanical'
  // contains 'mechanical', and an earlier automotive test on 'mechanical'
  // made this branch unreachable (every HVAC/plumbing/electrical card
  // rendered automotive orange and counted under the Automotive filter).
  // 'Mechanical' and 'HVAC & Mechanical' will be merged taxonomically in a
  // follow-up PR.
  if (
    c.includes('home improvement') || c.includes('hvac') ||
    c.includes('mechanical') || c.includes('plumbing') || c.includes('electrical')
  ) {
    return STYLES['home-improvement'];
  }
  if (c.includes('automotive') || c.includes('moving')) {
    return STYLES.automotive;
  }
  if (c.includes('construction') || c.includes('driveway') || c.includes('renovation')) {
    return STYLES.construction;
  }
  if (c.includes('cleaning')) return STYLES.cleaning;
  if (c.includes('emergency') || c.includes('restoration') || c.includes('repair')) {
    return STYLES.emergency;
  }
  if (c.includes('outdoor') || c.includes('renewable')) return STYLES.outdoor;
  if (c.includes('professional') || c.includes('photography') || c.includes('specialty')) {
    return STYLES.professional;
  }
  return STYLES.default;
}

/**
 * Templates flagged "Featured" or "New" in the gallery. Keep this list
 * tiny (3-5 ids) — the badge loses meaning if every card has it. The
 * three Wave AP-1 sample templates plus one anchor original.
 */
export const FEATURED_TEMPLATE_IDS = new Set<string>([
  'junk_removal_quote',
  'window_replacement_quote',
  'mold_remediation_quote',
  'roof_repair',
]);

/**
 * Helper — accent stripe geometry per layout. Single-column = full-width
 * top stripe; two-column = half-width left; multi-column = three small
 * stripes. Returned as raw style fragments so the mockup can paint them
 * via inline style without extra DOM complexity.
 */
export type StripeShape = 'full' | 'half' | 'triple';

export function stripeShapeForLayout(layout: string | undefined): StripeShape {
  if (layout === 'two-column') return 'half';
  if (layout === 'multi-column') return 'triple';
  return 'full';
}
